import * as React from 'react';
import { useState } from 'react';
import { 
  Button, 
  Checkbox, 
  //Radio,
  //RadioGroup,
  Textarea, 
  Input,
  CheckboxOnChangeData 
} from '@fluentui/react-components';
import { makeStyles, shorthands } from '@fluentui/react-components';
import { motion } from 'framer-motion';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useSharedStyles, inputStyle, primaryButtonStyle, labelStyle, errorTextStyle, textareaStyle } from './sharedStyles';
import { RSVP_CONSTANTS } from '../constants/rsvpConstants';
import { useLocation } from '../context/LocationContext';
import { getEventsForRSVPByLocation } from '../config/eventsConfig';

const useStyles = makeStyles({
  container: {
    maxWidth: '900px',
    margin: '4rem auto',
    padding: '0 1rem',
  },
  sectionTitle: {
    fontSize: '2rem',
    textAlign: 'center',
    color: 'var(--primary-color)',
    marginBottom: '1.5rem',
  },
  rsvpSection: {
    textAlign: 'left',
    background: 'rgba(255, 253, 248, 0.95)',
    padding: '2rem',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    maxWidth: '600px',
    margin: '0 auto',
  },
  confirmation: {
    textAlign: 'center',
    padding: '2rem',
    borderRadius: '10px',
  },
  highlight: {
    borderRadius: '10px',
    fontWeight: 600,
  },
  muted: {
    fontSize: '1rem',
    color: '#6d4c41',
  },
  sectionHeader: {
    color: 'var(--primary-color)', 
    fontSize: '1.3rem', 
    marginBottom: '1.5rem',
    borderBottom: '2px solid var(--primary-color)',
    paddingBottom: '0.5rem'
  },
  checkboxSection: {
    fontSize: '0.95rem',
    marginTop: '1rem',
    marginBottom: '1.5rem',
  },
  checkboxContainer: {
    padding: '1rem',
    ...shorthands.border('2px', 'solid', '#e0e0e0'),
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    transition: 'border-color 0.3s ease',
    selectors: {
      '&:focus-within': {
        ...shorthands.border('2px', 'solid', 'var(--primary-color)'),
        boxShadow: '0 0 0 3px var(--primary-light)',
      },
      '&:hover': {
        ...shorthands.border('2px', 'solid', '#c0c0c0'),
      },
    },
  },
  checkboxItem: {
    marginBottom: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    selectors: {
      '&:last-child': {
        marginBottom: '0',
      },
    },
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginBottom: '1.5rem',
  },
  radio: {
    marginBottom: '0.5rem',
    selectors: {
      '& .fui-Radio__indicator': {
        marginRight: '0.75rem',
        ...shorthands.border('2px', 'solid', '#e0e0e0'),
        backgroundColor: '#ffffff',
        transition: 'all 0.3s ease',
      },
      '&:hover .fui-Radio__indicator': {
        ...shorthands.border('2px', 'solid', 'var(--primary-color)'),
      },
      '& .fui-Radio__indicator[data-checked="true"]': {
        ...shorthands.border('2px', 'solid', 'var(--primary-color)'),
        backgroundColor: 'var(--primary-color)',
      },
      '& .fui-Radio__label': {
        fontSize: '1rem',
        fontWeight: 'normal',
        color: '#3a2a1a',
        cursor: 'pointer',
      },
    },
  },
});

const RSVPSection: React.FC = () => {
  const classes = useStyles();
  const sharedClasses = useSharedStyles();

  // Get current location from context
  const { currentLocation } = useLocation();
  
  // Get events and accommodation options filtered by location
  const enabledEvents = getEventsForRSVPByLocation(currentLocation);

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [guestsCount, setGuestsCount] = useState<number | ''>('');
  //const [favoriteSongChoice, setFavoriteSongChoice] = useState<'yes' | 'no' | null>(null);
  const [additionalInfo, setAdditionalInfo] = useState('');

  const onEventChange = (ev: React.ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) => {
    const { checked } = data!;
    const value = (ev.target as HTMLInputElement).value;
    setSelectedEvents((prev) =>
      checked ? [...prev, value] : prev.filter((ev) => ev !== value),
    );
    if (checked && formErrors.events) {
      setFormErrors(prev => ({ ...prev, events: '' }));
    }
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!fullName.trim()) {
      errors.fullName = RSVP_CONSTANTS.VALIDATION.FULL_NAME_REQUIRED;
    }
    
    if (selectedEvents.length === 0) {
      errors.events = RSVP_CONSTANTS.VALIDATION.EVENTS_REQUIRED;
    }
    
    if (!phoneNumber.trim()) {
      errors.phoneNumber = RSVP_CONSTANTS.VALIDATION.PHONE_REQUIRED;
    }
    
    // Email validation only if provided
    if (email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        errors.email = RSVP_CONSTANTS.VALIDATION.EMAIL_INVALID;
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
   try {
    await addDoc(collection(db, process.env.REACT_APP_DATABASE_NAME || 'rsvps-dev'), {
      name: fullName,
      events: selectedEvents,
      guestsCount: guestsCount === '' ? 0 : guestsCount,
      phoneNumber,
      email,
      //favoriteSongChoice,
      additionalInfo,
      createdAt: serverTimestamp(),
      userAgent: navigator.userAgent,
    });

    setSubmitted(true);
  } catch (err) {
    console.error('Error saving RSVP', err);
  } finally {
    setIsSubmitting(false);
  }
  };

  if (submitted) {
    return (
      <section id={RSVP_CONSTANTS.SECTION_ID} className={classes.container} aria-labelledby={RSVP_CONSTANTS.TITLE_ID}>
        <motion.div 
          className={classes.confirmation} 
          role="alert" 
          tabIndex={0}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <h2 id={RSVP_CONSTANTS.TITLE_ID}>{RSVP_CONSTANTS.CONFIRMATION_TITLE}</h2>
          <p>
            {RSVP_CONSTANTS.CONFIRMATION_MESSAGE_PREFIX}
            {selectedEvents.length > 0 ? (
              <span className={classes.highlight}>{RSVP_CONSTANTS.CONFIRMATION_MESSAGE_WITH_EVENTS_PREFIX}{selectedEvents.join(', ')}{RSVP_CONSTANTS.CONFIRMATION_MESSAGE_SUFFIX}</span>
            ) : (
              RSVP_CONSTANTS.CONFIRMATION_MESSAGE_SUFFIX
            )}
          </p>
        </motion.div>
      </section>
    );
  }

  return (
    <section id={RSVP_CONSTANTS.SECTION_ID} className={classes.container} aria-labelledby={RSVP_CONSTANTS.TITLE_ID}>
      <motion.form 
        onSubmit={onSubmit} 
        className={classes.rsvpSection} 
        aria-describedby={RSVP_CONSTANTS.DESC_ID}
        animate={{ opacity: isSubmitting ? 0.7 : 1 }}
        transition={{ duration: 0.3 }}
      >
        <h2 id={RSVP_CONSTANTS.TITLE_ID} style={{ color: 'var(--primary-color)' }}>{RSVP_CONSTANTS.SECTION_TITLE}</h2>
        <p className={classes.muted} id={RSVP_CONSTANTS.DESC_ID}>
          {RSVP_CONSTANTS.SECTION_DESCRIPTION}
        </p>

        {/* Required Questions Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 className={classes.sectionHeader}>
            {RSVP_CONSTANTS.REQUIRED_INFO_TITLE}
          </h3>

          {/* Full Name */}
          <label htmlFor="fullName" style={labelStyle}>
            {RSVP_CONSTANTS.FULL_NAME_LABEL}
          </label>
          <Input
            id="fullName"
            name="fullName"
            placeholder={RSVP_CONSTANTS.FULL_NAME_PLACEHOLDER}
            required
            value={fullName}
            onChange={e => {
              setFullName(e.target.value);
              if (formErrors.fullName) {
                setFormErrors(prev => ({ ...prev, fullName: '' }));
              }
            }}
            className={sharedClasses.formElementSpacing}
            style={{
              ...inputStyle,
              border: formErrors.fullName ? '2px solid var(--error-color)' : '2px solid #e0e0e0',
            }}
          />
          {formErrors.fullName && (
            <div style={errorTextStyle}>{formErrors.fullName}</div>
          )}

          {/* Events */}
          <div className={classes.checkboxSection}>
            <p>
              <strong style={{ color: 'var(--primary-color)'}}>{RSVP_CONSTANTS.EVENTS_LABEL}</strong>
            </p>
            {enabledEvents.map(event => (
              <div key={event.key} className={classes.checkboxItem}>
                <Checkbox
                  label={event.label}
                  value={event.label}
                  size='large'
                  onChange={onEventChange}
                  checked={selectedEvents.includes(event.label)}
                />
              </div>
            ))}
            {formErrors.events && (
              <div style={errorTextStyle}>{formErrors.events}</div>
            )}
          </div>

          {/* Contact info */}
          <label htmlFor="phoneNumber" style={labelStyle}>
            {RSVP_CONSTANTS.PHONE_LABEL}
          </label>
          <Input
            id="phoneNumber"
            name="phoneNumber"
            type="tel"
            placeholder={RSVP_CONSTANTS.PHONE_PLACEHOLDER}
            value={phoneNumber}
            onChange={e => {
              setPhoneNumber(e.target.value);
              if (formErrors.phoneNumber) {
                setFormErrors(prev => ({ ...prev, phoneNumber: '' }));
              }
            }}
            className={sharedClasses.formElementSpacing}
            style={{
              ...inputStyle,
              border: formErrors.phoneNumber ? '2px solid var(--error-color)' : '2px solid #e0e0e0',
            }}
          />
          {formErrors.phoneNumber && (
            <div style={errorTextStyle}>{formErrors.phoneNumber}</div>
          )}
        </div>

        {/* Optional Questions Section */}
        <div style={{ marginTop: '2rem' }}>
          <h3 className={classes.sectionHeader}>
            {RSVP_CONSTANTS.OPTIONAL_INFO_TITLE}
          </h3>

          {/* Guests */}
          <label htmlFor="guestsCount" style={labelStyle}>
            {RSVP_CONSTANTS.GUESTS_LABEL}
          </label>
          <Input
            id="guestsCount"
            name="guestsCount"
            type="number"
            min={RSVP_CONSTANTS.GUESTS_MIN}
            placeholder={RSVP_CONSTANTS.GUESTS_PLACEHOLDER}
            value={guestsCount.toString()}
            onChange={e => {
              const val = e.target.value;
              setGuestsCount(val === '' ? '' : Number(val));
            }}
            className={sharedClasses.formElementSpacing}
            style={inputStyle}
          />

          {/* Favorite song */}
          {/* <label style={labelStyle}>{RSVP_CONSTANTS.FAVORITE_SONG_LABEL}</label>
          <RadioGroup
            aria-labelledby="favorite-song-label"
            value={favoriteSongChoice ?? ''}
            onChange={(_, data) => setFavoriteSongChoice(data.value as 'yes' | 'no')}
            className={classes.radioGroup}
          >
            <Radio label={RSVP_CONSTANTS.FAVORITE_SONG_OPTIONS.YES.label} value={RSVP_CONSTANTS.FAVORITE_SONG_OPTIONS.YES.value} className={classes.radio} />
            <Radio label={RSVP_CONSTANTS.FAVORITE_SONG_OPTIONS.NO.label} value={RSVP_CONSTANTS.FAVORITE_SONG_OPTIONS.NO.value} className={classes.radio} />
          </RadioGroup> */}

          {/* Additional info */}
          <label htmlFor="additionalInfo" style={labelStyle}>
            {RSVP_CONSTANTS.ADDITIONAL_INFO_LABEL}
          </label>
          <Textarea
            id="additionalInfo"
            name="additionalInfo"
            placeholder={RSVP_CONSTANTS.ADDITIONAL_INFO_PLACEHOLDER}
            value={additionalInfo}
            onChange={e => setAdditionalInfo(e.target.value)}
            className={sharedClasses.formElementSpacing}
            rows={RSVP_CONSTANTS.TEXTAREA_ROWS}
            style={textareaStyle}
          />

          {/* Email */}
          <label htmlFor="email" style={labelStyle}>
            {RSVP_CONSTANTS.EMAIL_LABEL}
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder={RSVP_CONSTANTS.EMAIL_PLACEHOLDER}
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              if (formErrors.email) {
                setFormErrors(prev => ({ ...prev, email: '' }));
              }
            }}
            className={sharedClasses.formElementSpacing}
            style={{
              ...inputStyle,
              border: formErrors.email ? '2px solid var(--error-color)' : '2px solid #e0e0e0',
            }}
          />
          {formErrors.email && (
            <div style={errorTextStyle}>{formErrors.email}</div>
          )}
        </div>

        <Button 
          type="submit" 
          appearance="primary" 
          disabled={isSubmitting}
          style={{
            ...primaryButtonStyle,
            backgroundColor: isSubmitting ? '#d0d0d0' : 'var(--primary-color)',
            color: isSubmitting ? '#666666' : '#ffffff',
          }}
        >
          {isSubmitting && (
            <div className={sharedClasses.spinner}></div>
          )}
          {isSubmitting ? RSVP_CONSTANTS.BUTTON_SUBMITTING : RSVP_CONSTANTS.BUTTON_SUBMIT}
        </Button>
      </motion.form>
    </section>
  );
};

export default RSVPSection;